<script setup>
import { ref, onMounted } from 'vue';
import { useSettingsStore } from "@stores/settings";
import { usePrefetchImages } from "@composables/helpers/usePrefetchImages";

const config = useRuntimeConfig();
const CFIMAGE_URL = config.public.CLOUDFLARE_HOST_URL;

const settingsStore = useSettingsStore();
const { prefetchImages } = usePrefetchImages();

const isLoading = ref(true);
const isAgeVerificationModalVisible = ref(false);
const showRestrictionMessage = ref(false);
const modalBannerUrl = ref(null);
const showModal = ref(true);

const updateModalBannerUrl = async () => {
  const getUrl = (banner) => {
    if (!banner) return null;
    if (banner[0]?.image_cf) return CFIMAGE_URL + banner[0].image_cf + "/bannerModal";
    return banner[0]?.src || null;
  };

  const url = getUrl(settingsStore.state.modalBanners?.majority);
  
  if (url) {
    try {
      await prefetchImages([url]);
      modalBannerUrl.value = url;
    } catch (error) {
      console.error("Error prefetching image:", error);
      modalBannerUrl.value = null;
    }
  } else {
    modalBannerUrl.value = null;
  }
};

const hasBeenCheckedThisSession = () => {
  return localStorage.getItem('userAccepted') === 'true';
};

const markAsCheckedThisSession = () => {
  localStorage.setItem('userAccepted', 'true');
};

const handleYes = () => {
  markAsCheckedThisSession();
  isAgeVerificationModalVisible.value = false;
  showModal.value = false;
};

const handleNo = () => {
  showRestrictionMessage.value = true;
};

const closeModal = () => {
  showModal.value = false;
  isAgeVerificationModalVisible.value = false;
};

onMounted(async () => {
  await updateModalBannerUrl();
  
  setTimeout(() => {
    isAgeVerificationModalVisible.value = !hasBeenCheckedThisSession();
    isLoading.value = false;
  }, 500);
});
</script>

<template>
  <div id="age-verification">
    <div
      v-if="!isLoading && isAgeVerificationModalVisible"
      class="fixed inset-0 z-[100] bg-brand-background/65 h-screen w-screen"
    >
      <div
        class="fixed inset-0 transition-all duration-300"
        :class="[
          showRestrictionMessage
            ? 'backdrop-blur-lg bg-brand-background/60'
            : 'backdrop-blur-md bg-brand-background/30'
        ]"
      ></div>

      <transition name="fade" mode="out-in">
        <div
          v-if="showModal"
          class="scrollbar-hide fixed inset-0 flex items-center justify-center z-[100]"
        >
          <div class="bg-brand-background w-96 pb-3 rounded-md shadow-lg text-center overflow-hidden">
            <div
              v-if="modalBannerUrl"
              class="relative aspect-[75/32] w-full bg-secondary bg-cover bg-no-repeat bg-center after:absolute after:bottom-0 after:z-10 after:flex after:h-1/2 after:w-full after:bg-gradient-to-b after:from-transparent after:to-brand-background after:content-['']"
              :style="`background-image: url(${modalBannerUrl});`"
            ></div>

            <div
              v-else
              class="w-full bg-gradient rounded-t-md bg-gradient-to-b from-brand-separator via-brand-separator/20 to-brand-separator/5"
            >
              <BaseLogo class="px-16 py-8" />
            </div>

            <p v-if="showRestrictionMessage" class="p-5 text-xl font-medium text-white">
              O site é destinado para maiores de 18 anos.
            </p>

            <p v-else class="text-xl font-medium mb-4 p-5 text-white">
              Você possui mais de 18 anos?
            </p>

            <div class="flex gap-2 mx-5 mt-4 mb-3" v-if="!showRestrictionMessage">
              <button
                @click="handleNo"
                class="border border-gray-500 hover:border-gray-400 text-white w-full px-4 py-1 font-medium text-lg rounded transition"
              >
                Não
              </button>
              <button
                @click="handleYes"
                class="bg-success/75 text-white w-full px-4 py-1 font-medium text-lg rounded hover:brightness-110 transition"
              >
                Sim
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>